import { getEnvVar } from '../envUtils';
import ApiService from './ApiService';

/**
 * Service handling Telemetry REST APIs for telemetry dashboard configuration,
 * telemetry parameters catalog, all-panel telemetry data, and single-panel telemetry data.
 */
class TelemetryService {
    /**
     * Fetch telemetry dashboard configuration for a specific TrueTwin
     * GET test/truetwin/{truetwinId}/telemetrydashboard
     */
    static getDashboardConfig(truetwinId: string) {
        const baseUrl = getEnvVar('REACT_APP_BACKEND_BASE_URL');
        const url = `${baseUrl}test/truetwin/${truetwinId}/telemetrydashboard`;
        return ApiService.get(url)
            .then((res: any) => {
                return res?.data ?? res;
            })
            .catch((err: any) => {
                throw err.data ?? err;
            });
    }

    /**
     * Save/persist telemetry dashboard configuration for a specific TrueTwin
     * PUT test/truetwin/{truetwinId}/telemetrydashboard
     */
    static saveDashboardConfig(truetwinId: string, config: any) {
        const baseUrl = getEnvVar('REACT_APP_BACKEND_BASE_URL');
        const url = `${baseUrl}test/truetwin/${truetwinId}/telemetrydashboard`;
        return ApiService.put(url, config)
            .then((res: any) => {
                return res?.data ?? res;
            })
            .catch((err: any) => {
                throw err.data ?? err;
            });
    }

    /**
     * Fetch master dictionary of satellite telemetry parameters (subsystems, channels, units, limits)
     * GET test/telemetry/parameters
     */
    static getTelemetryParameters(truetwinId: string, commandCategory = "simulate") {
        const baseUrl = getEnvVar('REACT_APP_BACKEND_BASE_URL');
        const url = `${baseUrl}test/telemetry/truetwin/${truetwinId}/parameters?commandCategory=${commandCategory}`;
        return ApiService.get(url)
            .then((res: any) => {
                return res?.data ?? res;
            })
            .catch((err: any) => {
                throw err.data ?? err;
            });
    }

    /**
     * Fetch initial telemetry data for ALL panels for a specific TrueTwin
     * GET test/telemetry/{truetwinId}?startTime=...&endTime=...&resolution=...
     */
    static getAllPanelsTelemetryData(
        truetwinId: string,
        params?: { startTime?: number; endTime?: number; resolution?: string }
    ) {
        const baseUrl = getEnvVar('REACT_APP_BACKEND_BASE_URL');
        let url = `${baseUrl}test/telemetry/${truetwinId}`;
        if (params) {
            const query = new URLSearchParams();
            if (params.startTime != null) query.append('startTime', String(Math.floor(params.startTime)));
            if (params.endTime != null) query.append('endTime', String(Math.floor(params.endTime)));
            if (params.resolution) query.append('resolution', params.resolution);
            const queryString = query.toString();
            if (queryString) url += `?${queryString}`;
        }
        return ApiService.get(url)
            .then((res: any) => {
                return res?.data ?? res;
            })
            .catch((err: any) => {
                throw err.data ?? err;
            });
    }

    /**
     * Fetch telemetry data for a SINGLE panel for a specific TrueTwin
     * GET test/telemetry/{truetwinId}/panel/{panelId}?startTime=...&endTime=...&resolution=...
     */
    static getSinglePanelTelemetryData(
        truetwinId: string,
        panelId: string,
        params?: { startTime?: number; endTime?: number; resolution?: string }
    ) {
        const baseUrl = getEnvVar('REACT_APP_BACKEND_BASE_URL');
        let url = `${baseUrl}test/telemetry/${truetwinId}/panel/${panelId}`;
        if (params) {
            const query = new URLSearchParams();
            if (params.startTime != null) query.append('startTime', String(Math.floor(params.startTime)));
            if (params.endTime != null) query.append('endTime', String(Math.floor(params.endTime)));
            if (params.resolution) query.append('resolution', params.resolution);
            const queryString = query.toString();
            if (queryString) url += `?${queryString}`;
        }
        return ApiService.get(url)
            .then((res: any) => {
                return res?.data ?? res;
            })
            .catch((err: any) => {
                throw err.data ?? err;
            });
    }
}

export default TelemetryService;
